import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatter';

function ProjectionOverview() {
  const [showMethodology, setShowMethodology] = useState(false);
  const [showRisks, setShowRisks] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Executive Summary */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-3">Executive Summary</h2>
        <p className="text-gray-700 leading-relaxed">
          This projection models our path to achieving <span className="font-semibold">$20M ARR by H1 2026</span> and
          <span className="font-semibold"> $40M ARR by EoY</span>, starting from $10M (end of 2025). The model is based on
          actual FY25 performance data and conservative growth assumptions.
        </p>
      </div>

      {/* Target Goals & Requirements */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Target Goals & Requirements</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-50 border-b-2 border-blue-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Target</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">Starting ARR</td>
                <td className="px-4 py-3 font-semibold text-blue-600">{formatCurrency(10000000)}</td>
                <td className="px-4 py-3 text-gray-600">Dec 2025</td>
              </tr>
              <tr className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">H1 2026 Target</td>
                <td className="px-4 py-3 font-semibold text-green-600">{formatCurrency(20000000)}</td>
                <td className="px-4 py-3 text-gray-600">+{formatCurrency(10000000)} new ARR</td>
              </tr>
              <tr className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">FY26 Target (EoY)</td>
                <td className="px-4 py-3 font-semibold text-green-600">{formatCurrency(40000000)}</td>
                <td className="px-4 py-3 text-gray-600">+{formatCurrency(30000000)} new ARR</td>
              </tr>
              <tr className="bg-purple-50">
                <td className="px-4 py-3 font-medium">Total Closed-Won Needed</td>
                <td className="px-4 py-3 font-semibold text-purple-600">159 deals</td>
                <td className="px-4 py-3 text-gray-600">~13 deals/month avg</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Core Assumptions */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div>
          <h3 className="text-lg font-bold mb-4">Core Assumptions</h3>
          <p className="text-xs text-gray-500 mb-3">(Based on FY25 Actuals)</p>
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-blue-50 to-transparent p-3 rounded border-l-4 border-blue-500">
              <div className="text-xs font-semibold text-gray-600">ARPA (ASP)</div>
              <div className="text-lg font-bold text-blue-600">$194K</div>
              <div className="text-xs text-gray-500">Feb-Oct FY2025</div>
            </div>
            <div className="bg-gradient-to-r from-indigo-50 to-transparent p-3 rounded border-l-4 border-indigo-500">
              <div className="text-xs font-semibold text-gray-600">SQL → SQO</div>
              <div className="text-lg font-bold text-indigo-600">43.4%</div>
              <div className="text-xs text-gray-500">Conversion Rate</div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-transparent p-3 rounded border-l-4 border-green-500">
              <div className="text-xs font-semibold text-gray-600">SQO → CW</div>
              <div className="text-lg font-bold text-green-600">28.8%</div>
              <div className="text-xs text-gray-500">Conversion Rate</div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-transparent p-3 rounded border-l-4 border-purple-500">
              <div className="text-xs font-semibold text-gray-600">Pipeline Coverage Ratio</div>
              <div className="text-lg font-bold text-purple-600">3.5x</div>
              <div className="text-xs text-gray-500">Coverage Multiple</div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold mb-4">Pipeline Requirements</h3>
          <p className="text-xs text-gray-500 mb-3">(At 3.5x coverage ratio)</p>
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-orange-50 to-transparent p-3 rounded border-l-4 border-orange-500">
              <div className="text-xs font-semibold text-gray-600">Pipeline Needed</div>
              <div className="text-lg font-bold text-orange-600">$109M</div>
              <div className="text-xs text-gray-500">For $31M new ARR</div>
            </div>
            <div className="bg-gradient-to-r from-cyan-50 to-transparent p-3 rounded border-l-4 border-cyan-500">
              <div className="text-xs font-semibold text-gray-600">SQOs Required</div>
              <div className="text-lg font-bold text-cyan-600">561</div>
              <div className="text-xs text-gray-500">Sales Qualified Opportunities</div>
            </div>
            <div className="bg-gradient-to-r from-rose-50 to-transparent p-3 rounded border-l-4 border-rose-500">
              <div className="text-xs font-semibold text-gray-600">SQLs Required</div>
              <div className="text-lg font-bold text-rose-600">1,298</div>
              <div className="text-xs text-gray-500">Sales Qualified Leads</div>
            </div>
          </div>
        </div>
      </div>

      {/* Projection Methodology */}
      <div className="mb-8">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full flex items-center justify-between bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
        >
          <h3 className="text-lg font-bold">Projection Methodology</h3>
          <span className="text-xl">{showMethodology ? '▼' : '▶'}</span>
        </button>
        {showMethodology && (
          <div className="bg-gray-50 rounded-lg p-6 mt-4">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">1</span>
              Conversion Rates (Jan-Oct 2025)
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>SQL to SQO: <strong>43.4%</strong></span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>SQO to Closed Won: <strong>28.8%</strong></span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2">•</span>
                <span>SQL to Win: <strong>12.5%</strong></span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">2</span>
              Growth Model
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              ~10% monthly growth from <strong>52 SQL/month</strong> (Oct 2025) to <strong>201 SQL/month</strong> (Dec 2026)
            </p>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">3</span>
              Avg. Deal Size
            </h4>
            <p className="text-sm text-gray-700">
              <strong>$194K</strong> (based on FY25 closed deals)
            </p>
          </div>
        </div>
          </div>
        )}
      </div>

      {/* Key Risks & Factors */}
      <div className="mb-8">
        <button
          onClick={() => setShowRisks(!showRisks)}
          className="w-full flex items-center justify-between bg-yellow-50 rounded-lg p-4 hover:bg-yellow-100 transition-colors"
        >
          <h3 className="text-lg font-bold">Key Risks & Monitoring Factors</h3>
          <span className="text-xl">{showRisks ? '▼' : '▶'}</span>
        </button>
        {showRisks && (
          <div className="grid grid-cols-1 gap-4 mt-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <h4 className="font-semibold text-gray-800">Conversion Rates</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Monitor monthly; adjust pipeline if rates drop below <strong>43.4% (SQL→SQO)</strong> or <strong>28.8% (SQO→CW)</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <h4 className="font-semibold text-gray-800">Deal Size (ASP)</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Track quarterly; adjust deal count if average shifts from <strong>$194K</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <h4 className="font-semibold text-gray-800">Pipeline Coverage</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Increase to <strong>4x</strong> if sales cycles increase beyond current expectations
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <h4 className="font-semibold text-gray-800">Seasonality</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    December includes <strong>25-deal surge</strong> for year-end budgets
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectionOverview;
